<template>
  <div
    :class="{ 'd-none': hidden, 'd-flex': !hidden, 'drop-zone-active': isDragging }"
    class="flex-column flex-grow-1"
    style="height: calc(100vh - 152px);"
    @dragover.prevent
    @dragenter.prevent="isDragging = true"
    @dragleave.prevent="isDragging = false"
    @drop.prevent="handleDrop"
  >
    <div
      id="ai-chat-messages"
      ref="messages"
      class="flex-grow-1 card mb-2"
    >
      <div class="card-body">
        <div
          v-if="messages.length"
          class="pb-4"
        >
          <div
            v-for="(message, index) in messages"
            :id="`message-${index}`"
            :key="index"
            :class="['message', `message-${message.role}`]"
          >
            <div
              v-if="message.reOrderedProductImages"
              class="row mb-2"
            >
              <div class="col-12 mb-2">
                <p class="text-muted">
                  New suggested order:
                </p>
              </div>
              <div
                v-for="(productImage, productIndex) in message.reOrderedProductImages"
                :key="productIndex"
                class="col-6 p-0"
              >
                <div style="position: relative; width: 100%; padding-top: 100%;">
                  <img
                    v-if="productImage && productImage.url"
                    :src="productImage.url"
                    :title="productImage && productImage.id ? productImage.id : ''"
                    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain; object-position: center;"
                  >
                </div>
              </div>
            </div>
            <img
              v-if="message.imageUrl"
              class="mb-2"
              :src="message.imageUrl"
              style="width: 200px; height: 200px; object-fit: contain; object-position: right;"
            >
            <p
              class="message-content"
              v-html="message.text"
            />
            <div
              v-if="getFilteredImages(message).length > 0"
              class="row mt-3 px-2"
            >
              <div
                v-for="image in getFilteredImages(message)"
                :key="image.id"
                class="col-6 px-1 py-1"
              >
                <img
                  :src="image.image_url.url"
                  style="width: 100%; height: 100%; object-fit: contain; object-position: center;"
                >
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="w-100 h-100 d-flex flex-column align-items-center"
        >
          <p class="h5">
            How can I assist you?
          </p>
          <p class="text-muted text-center">
            I can analyze templates and suggest improvements.
          </p>
          <template v-if="userInputImages && userInputImages.length > 0">
            <div
              v-if="userInputImages.length === 1"
              class="position-relative"
            >
              <button
                class="remove-image-button"
                @click="removeImage(0)"
              >
                <i
                  class="fas fa-times"
                />
              </button>
              <img
                :src="userInputImages[0].image_url.url"
                style="width: 300px; height: 300px; object-fit: contain; object-position: center;"
              >
            </div>
            <div
              v-else
              class="row pb-4"
              style="width: 282px;"
            >
              <div
                v-for="(image, index) in userInputImages"
                :key="index"
                class="col-6 px-0"
              >
                <div
                  class="relative p-1"
                  style="width: 141px; height: 141px;"
                >
                  <button
                    class="remove-image-button"
                    @click="removeImage(index)"
                  >
                    <i
                      class="fas fa-times"
                    />
                  </button>
                  <img
                    :src="image.image_url.url"
                    style="width: 100%; height: 100%; object-fit: contain; object-position: center;"
                  >
                </div>
              </div>
            </div>
          </template>
          <div class="pt-2 pb-4 flex justify-center items-center">
            <button
              v-if="(selectedPrompt || userInputImages.length) && (selectedPrompt?.includeAllTemplatesImages || selectedPrompt?.includeSelectedTempalteImage)"
              type="button"
              class="btn btn-primary btn-sm"
              @click="resetUserInputImages()"
            >
              {{ userInputImages.length ? 'Clear images' : (selectedPrompt && selectedPrompt?.includeAllTemplatesImages ? 'Add template images' : 'Add selected template') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="flex-shrink-0 mt-2">
      <div class="d-flex flex-row align-items-center">
        <div class="btn-group dropup mr-2">
          <button
            type="button"
            class="btn btn-primary btn-sm dropdown-toggle"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            data-boundary="window"
            data-bs-popper="static"
          >
            Analyze
          </button>
          <div class="dropdown-menu scrollable-dropdown">
            <template v-for="(prompt, index) in customPrompts">
              <a
                :key="index"
                class="dropdown-item"
                style="width: 340px; white-space: normal;"
                href="javascript:void(0);"
                @click="selectPrompt(prompt)"
              >{{ prompt.name }}</a>
            </template>
          </div>
        </div>
        <a
          href="javascript:void(0);"
          class="mr-2"
          @click="toggleSidebarExpand()"
        >
          {{ sidebarExpanded ? 'Collapse' : 'Expand' }}
        </a>
        <div class="flex-grow-1 text-right">
          <a
            href="javascript:void(0);"
            @click="clearChat()"
          >
            New chat
          </a>
        </div>
      </div>
      <b-input-group class="mt-2">
        <b-textarea
          v-model="userInput"
          placeholder="Type your message..."
          :disabled="loading"
          max-rows="8"
          @keydown="handleEnterKey"
        />
        <b-button
          v-if="messages.length === 0"
          variant="secondary"
          size="sm"
          :disabled="loading"
          class="ml-1"
          @click="$refs.imageInput.click()"
        >
          <i class="fas fa-image" />
        </b-button>
        <input
          ref="imageInput"
          type="file"
          accept="image/*"
          multiple
          class="d-none"
          @change="handleImageSelect"
        >
        <b-button
          variant="primary"
          size="sm"
          :disabled="loading"
          class="ml-1"
          @click="beforeSendMessage"
        >
          <i class="cil-send" />
        </b-button>
      </b-input-group>
    </div>
    <div
      v-if="loading"
      class="position-absolute w-100 h-100 bg-white d-flex flex-column align-items-center py-4 z-3"
    >
      <div
        class="spinner-border"
        role="status"
      >
        <span class="sr-only">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
    HORIZONTAL_GUIDE_LINE_PREFIX_ID,
    VERTICAL_GUIDE_LINE_PREFIX_ID,
} from "@frontend/constants/templates-field-prefixes";
import { jsPDF } from "jspdf";
const showdown = require('showdown');
const CUSTOM_PROMPT_TYPE_ANALYTICS = 3;

export default {
    name: "AiAnalystTab",
    props: {
        targetElement: {
            type: fabric.Object,
            require: true,
            default: () => {},
        },
        isTabActive: {
            type: Boolean,
            default: false,
        },
        hidden: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        sidebarExpanded: false,
        isDragging: false,
        messages: [],
        userSelectedPromptId: null,
        userInput: "",
        userInputImages: [],
        loading: false,
        layersOrdered: [],
        customPrompts: [
            'loading'
        ]
    }),
    computed: {
        ...mapGetters("layout", ["layers", "target", "selectedInstance"]),
        prompts() {
            return [...this.customPrompts];
        },
        getFilteredImages() {
            return (message) => {
                if (!message.content || !Array.isArray(message.content)) return [];
                return message.content.filter(image => image.type === 'image_url' && image.image_url);
            };
        },
        selectedPrompt() {
            return this.prompts.find((prompt) => prompt.id === this.userSelectedPromptId);
        }
    },
    watch: {
        isTabActive(newVal) {
            if (!newVal) {
                this.sidebarExpanded = false;
                $('.b-sidebar').css('width', '430px');
            }
        }
    },
    mounted() {
        document.addEventListener('paste', this.handlePaste);
        this.loadCustomPrompts();
    },
    beforeDestroy() {
        document.removeEventListener('paste', this.handlePaste);
    },
    methods: {
        handleEnterKey(event) {
            if (event.key === 'Enter') {
                if (event.shiftKey) {
                    event.preventDefault();
                    const textarea = event.target;
                    const { selectionStart: start, selectionEnd: end } = textarea;
                    const before = this.userInput.slice(0, start);
                    const after = this.userInput.slice(end);
                    this.userInput = before + '\n' + after;
                    this.$nextTick(() => {
                        textarea.selectionStart = textarea.selectionEnd = start + 1;
                    });
                } else {
                    event.preventDefault();
                    this.beforeSendMessage();
                }
            }
        },
        handleDrop(event) {
            this.isDragging = false;
            const files = Array.from(event.dataTransfer.files).filter(file => file.type.startsWith('image/'));
            if (files.length > 0) {
                this.processFiles(files);
            }
        },
        processFiles(files) {
            const readFile = (file) => {
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        resolve({
                            type: 'image_url',
                            image_url: {
                                url: e.target.result
                            }
                        });
                    };
                    reader.readAsDataURL(file);
                });
            };

            Promise.all(files.map(readFile))
                .then(images => {
                    this.userInputImages.push(...images);
                });
        },
        toggleSidebarExpand() {
            this.sidebarExpanded = !this.sidebarExpanded;
            if (this.sidebarExpanded) {
                $('.b-sidebar').css('width', '860px');
            } else {
                $('.b-sidebar').css('width', '430px');
            }
        },
        handlePaste(event) {
            const items = (event.clipboardData || event.originalEvent.clipboardData).items;
            for (const item of items) {
                if (item.type.indexOf('image') === 0) {
                    const blob = item.getAsFile();
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        this.userInputImages.push({
                            type: 'image_url',
                            image_url: {
                                url: e.target.result
                            }
                        });
                    };
                    reader.readAsDataURL(blob);
                }
            }
        },
        async loadCustomPrompts() {
            this.customPrompts = [
                'loading'
            ];
            const response = await axios.get('/banner/groups/index_custom_prompts', {
                params: {
                    type: CUSTOM_PROMPT_TYPE_ANALYTICS,
                    page: 1,
                  paginate: false
                }
            });
            this.customPrompts = [];
            for (const prompt of response.data) {
                this.customPrompts.push({
                    id: prompt.id,
                    name: prompt.name,
                    prompt: prompt.prompt_start
                        .replaceAll("[current-date]", new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })),
                    analysisFormat: prompt.response_format && prompt.response_format.structuredOutputSchema ? prompt.response_format : null,
                    includeSelectedTempalteImage: prompt.settings && prompt.settings['include_selected_template_image'] ? true : false,
                    includeSelectedTemplateProductImages: prompt.settings && prompt.settings['include_selected_template_product_images'] ? true : false,
                    includeAllTemplatesImages: prompt.settings && prompt.settings['include_all_templates_images'] ? true : false,
                    unpackImagesOpenCV: prompt.settings && prompt.settings['unpack_images_opencv'] ? true : false
                });
            }
        },
        removeImage(index) {
            this.userInputImages.splice(index, 1);
        },
        async handleImageSelect(event) {
            const files = Array.from(event.target.files).filter(file => file.type.startsWith('image/'));
            if (files.length > 0) {
                await this.processFiles(files);
            }
            event.target.value = '';
        },
        async callOpenAI(settings) {
            const { data } = await axios.post(`/call-open-ai`, settings);
            return data;
        },
        reorderLayers(instanceId) {
            const usersFieldsOrder = JSON.parse(
                this.selectedInstance.data.users_fields_order || null,
            );
            this.selectedInstance.canvas.fire("layers:update", instanceId);
            if (usersFieldsOrder) {
                this.layersOrdered = _.sortBy(this.layers, (layer) =>
                    usersFieldsOrder.findIndex((id) => id === layer.id),
                );
                this.moveRulerGuideLinesToFront();
                return;
            }
            this.layersOrdered = this.layers;
            this.moveRulerGuideLinesToFront();
        },
        rerenderList() {
            if (this.isTabActive) {
                this.reorderLayers(this.selectedInstance.id);
            }
        },
        moveRulerGuideLinesToFront() {
            this.selectedInstance.canvas
                .getObjects()
                .filter((obj) =>
                    [
                        HORIZONTAL_GUIDE_LINE_PREFIX_ID,
                        VERTICAL_GUIDE_LINE_PREFIX_ID,
                    ].some((id) => obj.id.startsWith(id)),
                )
                .forEach((obj) => obj.bringToFront());
        },
        async resetUserInputImages() {
            this.selectedInstance.canvas.renderAll();

            if (this.userInputImages.length > 0 || !this.selectedPrompt) {
                this.userInputImages = [];
            } else if (this.selectedPrompt && this.selectedPrompt?.includeAllTemplatesImages) {
                const response = await axios.get('/banner/groups/' + $("#template-group-preview").attr("data-layout-id") + '/thumbnails');
                let missingThumbnails = false;
                const templatesDataUrls = response.data.filter((url) => {
                    if (url) {
                        return true;
                    } else {
                        missingThumbnails = true;
                        return false;
                    }
                }).map((url) => `/share?file=${url}`);
                this.userInputImages = templatesDataUrls.map((dataUrl) => ({
                    type: 'image_url',
                    image_url: {
                        url: dataUrl
                    }
                }));
                if (missingThumbnails) {
                    await Swal.fire({
                        title: 'Missing Thumbnails',
                        text: 'Some template thumbnails are missing. Please select set per page to "All" and save the layout to generate them.',
                        icon: 'warning',
                        showCancelButton: false,
                        confirmButtonText: 'Ok'
                    });
                }
            } else if (!this.selectedPrompt || this.selectedPrompt?.includeSelectedTempalteImage) {
                const dataURL = this.selectedInstance.canvas.toDataURL("image/png");
                this.userInputImages = [
                    {
                        type: 'image_url',
                        image_url: {
                            url: dataURL
                        }
                    }
                ];
            }
        },
        async selectPrompt(prompt) {
            let confirmed = true;
            if (this.messages.length > 0) {
                const result = await Swal.fire({
                    title: 'Change Prompt',
                    text: 'Choosing a new prompt will reset your current chat. Do you want to continue?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, change prompt',
                    cancelButtonText: 'No, keep current'
                });
                confirmed = result.value;
            }
            if (confirmed) {
                this.clearChat(prompt.id, false);
            }
        },
        clearChat(promptId = null, resetImages = true) {
            this.userSelectedPromptId = promptId;
            this.$nextTick(() => {
                this.messages = [];
                this.userInput = this.selectedPrompt?.prompt ?? "";
                if (resetImages) {
                    this.resetUserInputImages();
                }
            });
        },
        generatescoresSchema(components) {
            const schema = {};

            for (const [category, items] of Object.entries(components)) {
                schema[category] = {
                    "type": "object",
                    "properties": {},
                    "required": components[category],
                    "additionalProperties": false
                };

                items.forEach(item => {
                    schema[category].properties[item] = {
                        "type": "object",
                        "properties": {
                            "value": {
                                "type": "number",
                                "description": `A score for ${item} between 1 and 20.`
                            },
                            "recommendations": {
                                "type": "array",
                                "description": "A list of 3 recommendations for improving this score component of the banner ad. Keep each recommendation 5 words max.",
                                "items": {
                                    "type": "string"
                                }
                            }
                        },
                        "required": ["value", "recommendations"],
                        "additionalProperties": false
                    };
                });
            }

            return schema;
        },
        async generateProductDescriptionFromUrl(imageUrl) {
            try {
                const prompt = 'Describe the product shown in the following image in a maximum of 20 words.';

                const responseMessage = await this.callOpenAI({
                    messages: [
                        {
                            role: "system",
                            content: "You are an expert e-commerce product analyst AI.",
                        },
                        {
                            role: "user",
                            content: [
                                {
                                    type: 'text',
                                    text: prompt
                                },
                                ...(imageUrl ? [{
                                    type: 'image_url',
                                    image_url: {
                                        url: imageUrl
                                    }
                                }] : []),
                            ],
                            text: prompt,
                            imageUrl: imageUrl
                        }
                    ],
                    model: "gpt-4o"
                });

                return responseMessage.content;
            } catch (error) {
                console.error("Failed to generate product description:", error);
                return "Sorry, an error occurred while generating the product description.";
            }
        },
        async downloadImageAsDataURL(imageUrl, reduceQuality = false) {
            let response = await fetch(imageUrl);

            if (!response.ok) {
                response = await fetch('/share?file=' + imageUrl);
                if (!response.ok) {
                    throw new Error(`Failed to fetch image: ${response.statusText}`);
                }
            }

            const blob = await response.blob();

            if (!reduceQuality) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            }

            // If reduceQuality is true, resize image
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    const aspectRatio = img.height / img.width;
                    const newWidth = 640;
                    const newHeight = newWidth * aspectRatio;

                    const canvas = document.createElement('canvas');
                    canvas.width = newWidth;
                    canvas.height = newHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, newWidth, newHeight);

                    resolve(canvas.toDataURL('image/jpeg', 0.8)); // You can tweak quality here (0-1)
                };
                img.onerror = reject;
                img.src = URL.createObjectURL(blob);
            });
        },
        isValidJSON(jsonString) {
            try {
                JSON.parse(jsonString);
                return true;
            } catch (e) {
                return false;
            }
        },
        async beforeSendMessage() {
            if (this.userInputImages.length === 0) {
                await this.resetUserInputImages();
            }

            if (this.userInputImages.length === 0) {
                toastr.error('Please add images to analyze.');
                return;
            }

            if (this.selectedPrompt?.analysisFormat?.type === 'visual-playbook') {
                const urls = [
                    'https://www.walmart.com/ip/Mielle-Rosemary-Mint-Nourishing-Strengthening-Daily-Shampoo-with-Biotin-12-fl-oz-All-Hair-Types/557873948?classType=REGULAR&adsRedirect=true',
                    'https://www.walmart.com/ip/Soapbox-Biotin-Superfruit-Volumizing-Shampoo-with-Elderberry-and-Plum-Oil-16-fl-oz/906528136?classType=REGULAR&adsRedirect=true',
                    'https://www.walmart.com/ip/Mielle-Organics-Rosemary-Mint-Scalp-Hair-Strengthening-Oil-2-oz/828017004?classType=REGULAR&adsRedirect=true',
                    'https://www.walmart.com/ip/Hi-Pro-Pac-Intense-Protein-Treatment-to-Repair-Extremely-Damaged-Hair-1-75-fl-oz/176017051?classType=VARIANT&athbdg=L1200&from=/search'
                ];

                const responses = [];
                const allImages = [];

                for (const url of urls) {
                    toastr.info(`Generating individual product analysis (${urls.indexOf(url) + 1} of ${urls.length})`);
                    this.clearChat(this.selectedPrompt.id, true);
                    this.userInput = this.selectedPrompt.prompt.replaceAll('[iterated-url]', url);
                    const messageResponse = await this.sendMessage(false);
                    responses.push(messageResponse);
                    const messageImages = this.userInputImages.map(image => image.image_url.url);
                    allImages.push(messageImages);
                }

                toastr.info(`Consolidating product analyses into a playbook.`);
                this.clearChat(this.selectedPrompt.id, true);
                this.userInput = `Consolidate the ${allImages[0].length} images from each of the image carousels from the following products (that are best practice examples) into one single playbook with best practices for each of the ${allImages[0].length} images.\n\n` +
                    responses.map((response, index) => `Analyzed product ${index + 1}:\n${response}`).join('\n\n');
                
                this.selectedPrompt.analysisFormat.structuredOutputSchema = {
                    "name": "visual_playbook",
                    "schema": {
                        "type": "object",
                        "required": [
                            "image_carousel"
                        ],
                        "properties": {
                            "image_carousel": {
                                "type": "array",
                                "items": {
                                    "type": "object",
                                    "required": [
                                        "image_types",
                                        "image_notes",
                                        "image_description"
                                    ],
                                    "properties": {
                                        "image_notes": {
                                            "type": "array",
                                            "items": {
                                                "type": "string"
                                            },
                                            "description": "Based on the notes of each product, write up to 4 general comments (less than 8 words each) or best practices relevant to the image type and visual composition."
                                        },
                                        "image_types": {
                                            "type": "array",
                                            "items": {
                                                "type": "string"
                                            },
                                            "description": "The types of each product for this image. Example: image_type of product 1, image_type of product 2, etc. If types are repeated, add them only once."
                                        },
                                        "image_description": {
                                            "type": "string",
                                            "description": "Description of the image type. For example: 'Clean product on white background, high resolution, centered'"
                                        }
                                    },
                                    "additionalProperties": false
                                },
                                "description": `A list of types, notes and descriptions for exactly ${allImages[0].length} images.`
                            }
                        },
                        "additionalProperties": false
                    },
                    "strict": true
                };

                await this.sendMessage(true, allImages);
            } else {
                await this.sendMessage(true);
            }
        },
        async sendMessage(sendToChat = true, overrideReportImages = null) {
            if (!this.userInput.trim() || this.loading) return;

            let prompt = this.userInput.trim();
            let userMessage = null;
            let responseFormat = null;
            let productImages = [];

            this.loading = true;

            await this.$nextTick();

            if (this.messages.length === 0) {
                if (this.selectedPrompt?.includeSelectedTemplateProductImages) {
                    for (const layer of this.layersOrdered) {
                        if (layer.relatedCanvasObject && layer.relatedCanvasObject.fieldType === 'Product Image') {
                            const imageUrl = layer.relatedCanvasObject.getSrc();
                            const imageDataUrl = await this.downloadImageAsDataURL(imageUrl, true);
                            const imageDescription = await this.generateProductDescriptionFromUrl(imageDataUrl);
                            productImages.push({
                                id: layer.relatedCanvasObject.fileName,
                                url: imageUrl,
                                description: imageDescription
                            });
                        }
                    }

                    prompt += '\n\nList of product ids for the products shown in the image:\n' + 
                        (productImages.length > 0 
                            ? productImages.map(productImage => `ID: ${productImage.id}\nDescription: ${productImage.description}`).join('\n\n')
                            : 'No products found');
                    responseFormat = {
                        "name": "scores",
                        "schema": {
                            "type": "object",
                            "properties": {
                                "product_ids": {
                                    "type": "array",
                                    "description": "The newly ordered list of product ids.",
                                    "items": {
                                        "type": "string"
                                    }
                                },
                                "rationale": {
                                    "type": "string",
                                    "description": "The rationale for why the new product order is an improvement. Use between 20 and 60 words."
                                }
                            },
                            "additionalProperties": false,
                            "required": [
                                "product_ids",
                                "rationale"
                            ]
                        },
                        "strict": true
                    };
                }

                const urlRegex = /(https?:\/\/[^\s]+)/g;
                const urls = prompt.match(urlRegex) || [];

                for (let i = 0; i < urls.length; i++) {
                    const url = urls[i];
                    const { data } = await axios.post(`/webscrape`, { url });
                    if (data && data.length > 0) {
                        for (const imageUrl of data) {
                            try {
                                this.userInputImages.push({
                                    type: 'image_url',
                                    image_url: { url: await this.downloadImageAsDataURL(imageUrl, true) }
                                });
                            } catch (error) {
                                console.error('Error processing image:', error);
                            }
                        }
                        
                        prompt = prompt.replaceAll(url, `${url} ${data.length} images`);
                        toastr.success(`Successfully processed URL: ${data.length} images found`);
                    } else {
                        toastr.warning(`No images found at URL: ${url}`);
                    }
                }

                let imageContents = [];

                if (this.userInputImages.length === 1 && this.selectedPrompt?.unpackImagesOpenCV) {
                    try {
                        const { data } = await axios.post(`/unpack-images-open-cv`, {
                            image_url: await this.downloadImageAsDataURL(this.userInputImages[0].image_url.url, true)
                        });
                        this.userInputImages = data.map(item => ({
                            type: 'image_url',
                            image_url: {
                                url: item
                            }
                        }));
                        imageContents = this.userInputImages;
                    } catch (error) {
                        console.error("Failed to unpack images:", error);
                        await Swal.fire({
                            title: 'Error',
                            text: 'Failed to unpack images.',
                            icon: 'error',
                            confirmButtonText: 'Ok'
                        });
                        this.loading = false;
                        return;
                    }
                } else {
                    imageContents = await Promise.all(this.userInputImages.map(async image => ({
                        type: 'image_url',
                        image_url: {
                            url: await this.downloadImageAsDataURL(image.image_url.url, true)
                        }
                    })));
                }

                userMessage = {
                    role: "user",
                    content: [
                        {
                            type: 'text',
                            text: prompt
                        },
                        ...imageContents
                    ],
                    text: this.userInput.trim(),
                    imageUrl: this.userInputImage
                };
            } else {
                userMessage = {
                    role: "user",
                    content: this.userInput.trim(),
                    text: this.userInput.trim()
                };
            }

            if (this.selectedPrompt?.analysisFormat) {
                responseFormat = this.selectedPrompt?.analysisFormat.structuredOutputSchema;
            }

            this.messages.push(userMessage);

            let responseMessage = null;

            try {
                responseMessage = await this.callOpenAI({
                    messages: [
                        // Send only the latest 20 messages
                        ...this.messages.slice(-20).map((message) => ({
                            role: message.role,
                            content: message.content,
                        })),
                    ],
                    model: this.selectedPrompt?.model,
                    response_format: responseFormat
                });

                const tableClassExtension = () => [{
                    type: 'output',
                    regex: /<table>/g,
                    replace: '<table class="table table-bordered">'
                }];
                const converter = new showdown.Converter({
                    tables: true,
                    extensions: [tableClassExtension]
                });

                if (sendToChat) {
                    let htmlContent = '';
                    const apiSettingsForLogging = {
                        messages: [
                            ...this.messages.slice(-20).map((message) => ({
                                role: message.role,
                                content: message.content,
                            })),
                        ],
                        model: this.selectedPrompt?.model,
                        response_format: responseFormat
                    };

                    let analysisType = null;
                    let analysisResponse = null;

                    if (this.selectedPrompt?.analysisFormat?.structuredOutputSchema && this.isValidJSON(responseMessage.content)) {
                        let jsonData = JSON.parse(responseMessage.content);
                        responseMessage.content = JSON.stringify(jsonData);
                        jsonData['images'] = overrideReportImages ? overrideReportImages : this.userInputImages.map(image => image.image_url.url);
                        analysisType = this.selectedPrompt?.analysisFormat?.type;
                        analysisResponse = await axios.post('/banner/groups/' + $("#template-group-preview").attr("data-layout-id") + '/analysis-reports', {
                            'api_response': responseMessage.content,
                            'api_call_data': JSON.stringify(apiSettingsForLogging),
                            'type': analysisType,
                            'data': jsonData
                        });
                    } else {
                        analysisType = "generic-markdown";
                        analysisResponse = await axios.post('/banner/groups/' + $("#template-group-preview").attr("data-layout-id") + '/analysis-reports', {
                            'api_response': responseMessage.content,
                            'api_call_data': JSON.stringify(apiSettingsForLogging),
                            'type': analysisType,
                            'data': {
                                title: this.selectedPrompt?.name,
                                markdown: responseMessage.content,
                                images: overrideReportImages ? overrideReportImages : this.userInputImages.map(image => image.image_url.url)
                            }
                        });
                    }

                    htmlContent = converter.makeHtml(`<div style="whitespace: nowrap;"><a href="${analysisUrl}" target="_blank">Open analysis in new tab</a></div>`);
                    this.messages.push({
                        role: "assistant",
                        content: responseMessage.content,
                        text: htmlContent
                    });

                    const analysisUrl = `/analysis-reports/${analysisResponse.data.id}/${analysisResponse.data.uuid}`;
                    window.open(analysisUrl, '_blank');
                }
                this.loading = false;
            } catch (error) {
                console.error("Error:", error);
                this.messages.push({
                    role: "assistant",
                    content:
                        "Sorry, there was an error processing your message.",
                });
                this.loading = false;
            } finally {
                this.$nextTick(() => {
                    const messagesDiv = this.$refs.messages;
                    if (messagesDiv) {
                        messagesDiv.scrollTop = messagesDiv.scrollHeight;
                    }
                });
            }

            return responseMessage ? responseMessage.content : null;
        }
    }
};
</script>

<style scoped lang="scss">
.chat-module {
    height: calc(100vh - 98px);
}
.chat-module .card {
    position: relative;
    overflow-y: auto;
}
.message {
    margin: 10px;
    padding: 10px;
    border-radius: 10px;
}
.message p {
    margin-bottom: 0px;
    text-align: left;
    white-space: pre-line;
}
.message-user {
    background-color: #209c86;
    color: white;
}
.message-assistant {
    background-color: #e9ecef;
}
.remove-image-button {
    position: absolute;
    right: 10px;
    top: 10px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #333;
    border-radius: 50%;
}
.remove-image-button i {
    font-size: 10px;
    color: #fff;
}
.drop-zone-active {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.05);
    border: 2px dashed #666;
    pointer-events: none;
    z-index: 1;
  }
}

.scrollable-dropdown {
  max-height: calc(100% - 150px);
  overflow-y: auto;
  overflow-x: hidden;
}

</style>
